/* Native Evolve section enhancements. No dependencies or custom elements. */
(() => {
  'use strict';
  if (window.evcSections) {
    window.evcSections.init(document);
    return;
  }

  const instances = new Map();
  const motion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  const within = (scope, selector) => [...scope.querySelectorAll(selector)];
  let frame = 0;

  function updateSticky() {
    frame = 0;
    const purchase = document.getElementById('buy-box');
    const header = document.querySelector('.section-header');
    const headerBottom = header ? Math.max(0, header.getBoundingClientRect().bottom) : 73;
    within(document, '[data-evc-sticky]').forEach(bar => {
      const visible = purchase && purchase.getBoundingClientRect().bottom < headerBottom;
      bar.hidden = !visible;
      if (window.matchMedia('(min-width: 1000px)').matches) {
        bar.style.top = `${headerBottom}px`;
      } else {
        bar.style.removeProperty('top');
      }
    });
  }

  function queueSticky() {
    if (!frame) frame = requestAnimationFrame(updateSticky);
  }

  function setPrices(input) {
    if (!input) return;
    const selectedPrices = { price: input.dataset.price || '', compare: input.dataset.compare || '' };
    within(document, '[data-evc-sticky-price]').forEach(node => {
      const old = node.querySelector('s');
      const current = node.querySelector('span');
      if (old) {
        old.textContent = selectedPrices.compare;
        old.hidden = !selectedPrices.compare || selectedPrices.compare === selectedPrices.price;
      }
      if (current) current.textContent = selectedPrices.price;
    });
  }

  function initBuyBox(root, signal) {
    const inputs = within(root, '[data-evc-plan]');
    const buy = root.querySelector('[data-evc-buy]');
    const gift = root.querySelector('[data-evc-gift]');
    const unlock = root.querySelector('[data-evc-unlock]');
    const dialog = root.querySelector('.evc-ingredients-dialog');
    let returnFocus;
    let previousOverflow;

    function selectPlan(input) {
      if (!input) {
        if (buy) { buy.removeAttribute('href'); buy.setAttribute('aria-disabled', 'true'); }
        return;
      }
      if (buy) {
        buy.href = input.dataset.cart;
        buy.removeAttribute('aria-disabled');
      }
      const locked = input.value !== 'three';
      if (gift) gift.dataset.locked = String(locked);
      if (unlock) unlock.hidden = !locked || !inputs.some(plan => plan.value === 'three');
      setPrices(input);
      queueSticky();
    }

    if (!inputs.some(input => input.checked) && inputs[0]) inputs[0].checked = true;
    selectPlan(inputs.find(input => input.checked));
    root.addEventListener('change', event => {
      if (event.target.matches('[data-evc-plan]')) selectPlan(event.target);
    }, { signal });
    if (unlock) unlock.addEventListener('click', () => {
      const quarterly = inputs.find(input => input.value === 'three');
      if (quarterly) { quarterly.checked = true; selectPlan(quarterly); quarterly.focus(); }
    }, { signal });

    // The gallery is a row the browser scrolls and snaps, so a swipe works and
    // no picture is ever swapped out from under the shopper.
    const track = root.querySelector('[data-evc-track]');
    const slides = within(root, '.evc-gallery-slide');
    const thumbs = within(root, '[data-evc-slide]');
    const strip = thumbs.length ? thumbs[0].parentElement : null;
    let shown = -1;

    function markSlide(index) {
      if (index === shown) return;
      shown = index;
      thumbs.forEach((item, i) => item.setAttribute('aria-pressed', String(i === index)));
      const thumb = thumbs[index];
      if (!thumb || !strip) return;
      const left = thumb.offsetLeft - strip.offsetLeft;
      if (left < strip.scrollLeft ||
          left + thumb.offsetWidth > strip.scrollLeft + strip.clientWidth) {
        strip.scrollTo({ left: left - 8, behavior: motion() });
      }
    }

    function showSlide(index) {
      const slide = slides[index];
      if (track && slide) {
        track.scrollTo({ left: slide.offsetLeft - slides[0].offsetLeft, behavior: motion() });
      }
      markSlide(index);
    }

    thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => showSlide(index), { signal });
      thumb.addEventListener('keydown', event => {
        let next;
        if (event.key === 'ArrowRight') next = (index + 1) % thumbs.length;
        if (event.key === 'ArrowLeft') next = (index - 1 + thumbs.length) % thumbs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = thumbs.length - 1;
        if (next === undefined) return;
        event.preventDefault();
        thumbs[next].focus({ preventScroll: true });
        showSlide(next);
      }, { signal });
    });

    if (track && slides.length > 1) {
      let galleryFrame = 0;
      track.addEventListener('scroll', () => {
        if (galleryFrame) return;
        galleryFrame = requestAnimationFrame(() => {
          galleryFrame = 0;
          const width = track.clientWidth || 1;
          const index = Math.round(track.scrollLeft / width);
          markSlide(Math.max(0, Math.min(slides.length - 1, index)));
        });
      }, { signal, passive: true });
    }
    markSlide(0);

    if (dialog && typeof dialog.showModal === 'function') {
      const open = root.querySelector('[data-evc-ingredients]');
      if (open) open.addEventListener('click', () => {
        if (dialog.open) return;
        returnFocus = document.activeElement;
        previousOverflow = document.documentElement.style.overflow;
        dialog.showModal();
        document.documentElement.style.overflow = 'hidden';
      }, { signal });
      dialog.addEventListener('click', event => {
        if (event.target.closest('[data-evc-close]')) dialog.close();
        else if (event.target === dialog) {
          const r = dialog.getBoundingClientRect();
          if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) dialog.close();
        }
      }, { signal });
      dialog.addEventListener('close', () => {
        document.documentElement.style.overflow = previousOverflow || '';
        if (returnFocus && returnFocus.isConnected) returnFocus.focus({ preventScroll: true });
      }, { signal });
    }
    return () => {
      if (dialog && dialog.open) {
        dialog.close();
        document.documentElement.style.overflow = previousOverflow || '';
      }
    };
  }

  function initMedia(root, signal) {
    const track = root.querySelector('[data-evc-videos]');
    if (!track) return () => {};
    const cards = within(track, '.evc-video-card');
    const videos = cards.map(card => card.querySelector('video'));
    let active = 0;
    let scrollTimer;

    const stopOthers = playing => videos.forEach(video => { if (video !== playing) video.pause(); });
    cards.forEach((card, index) => {
      const video = videos[index];
      const button = card.querySelector('[data-evc-play]');
      if (!video || !button) return;
      video.controls = false;
      button.hidden = false;
      const toggle = async () => {
        if (!video.paused) { video.pause(); return; }
        stopOthers(video);
        try { await video.play(); }
        catch (_) { video.controls = true; button.hidden = true; }
      };
      button.addEventListener('click', toggle, { signal });
      // While playing, native controls own clicks (including pause and seeking).
      // The overlay button owns initial playback and remains keyboard accessible.
      video.addEventListener('play', () => { stopOthers(video); button.hidden = true; button.setAttribute('aria-pressed', 'true'); video.controls = true; }, { signal });
      const paused = () => { button.hidden = false; button.setAttribute('aria-pressed', 'false'); video.controls = false; };
      video.addEventListener('pause', paused, { signal });
      video.addEventListener('ended', paused, { signal });
    });

    function select(index, behavior = motion()) {
      if (!cards.length) return;
      active = (index + cards.length) % cards.length;
      cards.forEach((card, i) => { card.dataset.active = String(i === active); });
      stopOthers(null);
      if (window.matchMedia('(min-width: 1170px)').matches && cards.length > 2) {
        // Keep the selected customer centered, with adjacent real blocks on each side.
        const previous = (active - 1 + cards.length) % cards.length;
        for (let i = 0; i < cards.length; i++) track.append(cards[(previous + i) % cards.length]);
      } else {
        cards.forEach(card => track.append(card));
        const card = cards[active];
        track.scrollTo({ left: card.offsetLeft - track.offsetLeft - (track.clientWidth - card.clientWidth) / 2, behavior });
      }
    }
    const prev = root.querySelector('[data-evc-video-prev]');
    const next = root.querySelector('[data-evc-video-next]');
    if (prev) prev.addEventListener('click', () => select(active - 1), { signal });
    if (next) next.addEventListener('click', () => select(active + 1), { signal });
    const resize = new ResizeObserver(() => select(active, 'auto'));
    resize.observe(track);
    track.addEventListener('scroll', () => {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        const middle = track.getBoundingClientRect().left + track.clientWidth / 2;
        let distance = Infinity;
        cards.forEach((card, i) => {
          const r = card.getBoundingClientRect();
          const d = Math.abs(r.left + r.width / 2 - middle);
          if (d < distance) { distance = d; active = i; }
        });
        cards.forEach((card, i) => { card.dataset.active = String(i === active); });
      }, 120);
    }, { signal, passive: true });
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => { if (!entry.isIntersecting) entry.target.pause(); });
    });
    videos.forEach(video => observer.observe(video));
    select(0, 'auto');
    return () => { clearTimeout(scrollTimer); resize.disconnect(); observer.disconnect(); stopOthers(null); };
  }

  function init(scope) {
    const roots = within(scope, '[data-evc-section]');
    if (scope.matches && scope.matches('[data-evc-section]')) roots.unshift(scope);
    roots.forEach(root => {
      if (instances.has(root)) return;
      const controller = new AbortController();
      let cleanup = () => {};
      if (root.matches('[data-evc-buy-box]')) cleanup = initBuyBox(root, controller.signal);
      if (root.dataset.evcSection === 'media-wall') cleanup = initMedia(root, controller.signal);
      instances.set(root, () => { cleanup(); controller.abort(); });
    });
    const selected = document.querySelector('[data-evc-plan]:checked');
    if (selected) setPrices(selected);
    queueSticky();
  }

  function destroy(scope) {
    instances.forEach((cleanup, root) => {
      if (root === scope || scope.contains(root)) { cleanup(); instances.delete(root); }
    });
    queueSticky();
  }

  window.evcSections = { init, destroy };
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => destroy(event.target));
  document.addEventListener('shopify:block:select', event => {
    const block = event.target;
    const thumb = block.matches('[data-evc-slide]') ? block : block.querySelector('[data-evc-slide]');
    if (thumb) thumb.click();
    const detail = block.matches('details') ? block : block.querySelector('details');
    if (detail) detail.open = true;
  });
  document.addEventListener('click', event => {
    const link = event.target.closest('.evc a[href="#buy-box"]');
    if (!link) return;
    const target = document.getElementById('buy-box');
    if (!target) return;
    event.preventDefault();
    target.scrollIntoView({ block: 'start', behavior: motion() });
  });
  // Zend's existing footer handler expects .accordion-content, while its real
  // markup uses .mobile-f-menu. Handle only this layout's footer toggle.
  document.addEventListener('click', event => {
    if (!document.body.classList.contains('evc-page')) return;
    const button = event.target.closest('.footer-promotional .footer-hamburger');
    if (!button) return;
    const details = button.closest('.footer-promotional').querySelector('.footer-links-mobile details');
    if (!details) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    details.open = !details.open;
    button.classList.toggle('active', details.open);
    button.setAttribute('aria-expanded', String(details.open));
  }, true);
  window.addEventListener('scroll', queueSticky, { passive: true });
  window.addEventListener('resize', queueSticky, { passive: true });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
})();
